import { useEffect, useMemo } from 'react';
import { useField } from '../../field/useField';
import { useInputController } from '../../hooks/useInputController';
import { StaticInput } from '../static/StaticInput';

export interface IComputedStaticTextInputProps<T> {
  name: string;
  value: T;
  removeBorder?: boolean;
  format?: (value: T) => string;
  /** A differenza di format non modifica il valore nel form ma è applicato solo la label mostrata */
  renderFormat?: (value: T) => string;
  readonly?: boolean;
  /** Permette di nascondere il componente pur mantenendo attive tutte le logiche */
  hidden?: boolean;
}

/**
 * Permette di renderizzare un valore come campo statico,
 * registrandone però il riferimento nel form.
 */
export function ComputedStaticTextInput<T>(
  props: IComputedStaticTextInputProps<T>,
) {
  const {
    name,
    value: providedComputedValue,
    removeBorder,
    format,
    renderFormat = value => value as string,
    readonly,
    hidden,
  } = props;

  // Non possiamo usare `undefined` come valore iniziale perché
  // impostandolo con `setValue` RHF lo _ignora_. Usiamo `null`
  const computedValue = providedComputedValue ?? null;

  const {
    field: { value },
  } = useInputController(name, { enabled: true });

  // Usiamo `setValue` per aggiornare il valore del campo
  // anziché `onChange` del controller perché possiamo impostare
  // il flage `shouldDirty` a `false` per evitare che il campo
  // "sporchi" il form al primo render (mostrando, altrimenti, l'alert in caso di cambio di pagina).
  const { setValue } = useField(name);

  useEffect(() => {
    if (readonly) return;
    if (value !== computedValue) {
      // Dobbiamo usare `setTimeout` per evitare che gli `useWatch` collegati
      // al campo abbiano tempo di registrare la subscription prima che il valore
      // venga impostato.
      // Vedi: https://github.com/react-hook-form/react-hook-form/issues/11056
      setTimeout(() => {
        setValue(computedValue, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }, 0);
    }
  }, [value, computedValue, readonly, setValue]);

  const formattedValue = useMemo(() => {
    if (readonly) {
      if (!format) return value ?? computedValue;
      if (value == null && computedValue == null) return null;
      return format(value ?? computedValue);
    }

    if (value == null) return null;
    if (!format) return value;
    return format(value);
  }, [value, format, readonly, computedValue]);

  if (hidden) return null;

  return (
    <StaticInput
      value={renderFormat(formattedValue ?? '')}
      removeBorder={removeBorder}
    />
  );
}
